import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { kpisApi } from '../services/apiService';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
interface KPIDeleteConfirmModalProps {
  visible: boolean;
  kpiId: number | null;
  kpiName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ImpactCounts {
  readings: number;
  schedules: number;
}

const WarningIcon = ({ size = 48, color = COLORS.danger }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M12 8V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M12 16H12.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function KPIDeleteConfirmModal({
  visible,
  kpiId,
  kpiName,
  onConfirm,
  onCancel,
}: KPIDeleteConfirmModalProps) {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const [impact, setImpact] = useState<ImpactCounts | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isExisting = typeof kpiId === 'number' && kpiId > 0;

  useEffect(() => {
    if (!visible) {
      setImpact(null);
      return;
    }
    if (!isExisting || kpiId === null) {
      setImpact({ readings: 0, schedules: 0 });
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setImpact(null);

    Promise.all([
      kpisApi.getReadings(kpiId).catch(() => [] as unknown[]),
      kpisApi.getSchedules(kpiId).catch(() => [] as unknown[]),
    ])
      .then(([readings, schedules]) => {
        if (cancelled) return;
        setImpact({
          readings: Array.isArray(readings) ? readings.length : 0,
          schedules: Array.isArray(schedules) ? schedules.length : 0,
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, kpiId, isExisting]);

  const themeColors = {
    container: colors.card,
    title: colors.textPrimary,
    message: colors.textMuted,
    impactBg: isDark ? colors.backgroundAlt : tokens.colors.raw.red["50"],
    impactBorder: isDark ? colors.border : '#FECACA',
    impactText: colors.textPrimary,
  };

  const totalImpact = (impact?.readings ?? 0) + (impact?.schedules ?? 0);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.overlay} onPress={onCancel}>
        <Pressable
          style={[styles.container, { backgroundColor: themeColors.container }]}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={[styles.iconContainer, { backgroundColor: `${COLORS.danger}15` }]}>
            <WarningIcon size={48} color={COLORS.danger} />
          </View>

          <Text style={[styles.title, { color: themeColors.title }]}>
            {t('kpis.deleteConfirmTitle', 'حذف المؤشر')}
          </Text>

          {kpiName ? (
            <Text style={[styles.kpiName, { color: themeColors.title }]} numberOfLines={2}>
              « {kpiName} »
            </Text>
          ) : null}

          <Text style={[styles.message, { color: themeColors.message }]}>
            {t('kpis.deleteWarning', 'سيتم حذف هذا المؤشر نهائياً ولا يمكن استرجاعه.')}
          </Text>

          {isExisting ? (
            <View style={[styles.impactBox, { backgroundColor: themeColors.impactBg, borderColor: themeColors.impactBorder }]}>
              {isLoading ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator size="small" color={COLORS.danger} />
                  <Text style={[styles.impactLoadingText, { color: themeColors.message }]}>
                    {t('common.loading', 'جاري التحميل...')}
                  </Text>
                </View>
              ) : impact && totalImpact > 0 ? (
                <>
                  <Text style={[styles.impactHeader, { color: themeColors.impactText }]}>
                    {t('kpis.deleteImpactHeader', 'سيتم حذف البيانات المرتبطة:')}
                  </Text>
                  {impact.readings > 0 && (
                    <Text style={[styles.impactItem, { color: themeColors.impactText }]}>
                      • {t('kpis.readingsCount', '{{count}} قراءة', { count: impact.readings })}
                    </Text>
                  )}
                  {impact.schedules > 0 && (
                    <Text style={[styles.impactItem, { color: themeColors.impactText }]}>
                      • {t('kpis.schedulesCount', '{{count}} جدولة قراءة', { count: impact.schedules })}
                    </Text>
                  )}
                </>
              ) : (
                <Text style={[styles.impactItem, { color: themeColors.message }]}>
                  {t('kpis.deleteNoImpact', 'لا توجد بيانات مرتبطة بهذا المؤشر.')}
                </Text>
              )}
            </View>
          ) : null}

          <View style={styles.buttonContainer}>
            <Button variant="secondary" onPress={onCancel} disabled={isLoading} style={{ flex: 1 }}>
              {t('common.cancel', 'إلغاء')}
            </Button>
            <Button variant="destructive" onPress={onConfirm} disabled={isLoading} style={{ flex: 1 }}>
              {t('common.delete', 'حذف')}
            </Button>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["5"],
  },
  container: {
    borderRadius: tokens.radii.absolute["radius-16"],
    padding: tokens.spacing.absolute["6"],
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
  },
  iconContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  title: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["1_5"],
  },
  kpiName: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["2_5"],
  },
  message: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
    lineHeight: 22,
  },
  impactBox: {
    width: '100%',
    borderRadius: tokens.radii.absolute["radius-10"],
    borderWidth: tokens.spacing.absolute["px"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  impactLoadingText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
  },
  impactHeader: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    marginBottom: tokens.spacing.absolute["1_5"],
  },
  impactItem: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    lineHeight: 20,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    width: '100%',
  },
});
